/**
 * Утилиты для интеграции с 2ГИС (веб-версия и мобильное приложение)
 */

export const normalizeDgisCoordinate = (value, min, max) => {
  if (value === null || value === undefined || String(value).trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= min && parsed <= max ? parsed : null;
};

/**
 * Базовые координаты региона (Самарская область: Самара — Кинель) для поиска адреса без координат
 */
const DEFAULT_SAMARA_REGION_VIEWPORT = '50.35,53.21/11';

/**
 * Регулярное выражение для обнаружения других крупных регионов/городов в адресе
 */
const OTHER_REGIONS_REGEX = /(?:^|[,\s])(москва|московская|санкт-петербург|петербург|питер|ленинградская|казань|татарстан|уфа|башкортостан|екатеринбург|новосибирск|нижний\s+новгород|краснодар|ростов)/i;

/**
 * Веб-ссылка на поиск адреса или точки в 2ГИС
 */
export const getDgisAddressUrl = (address, coordinates = null) => {
  const cleanAddress = String(address || '').trim();
  const lat = normalizeDgisCoordinate(coordinates?.latitude ?? coordinates?.lat, -90, 90);
  const lng = normalizeDgisCoordinate(coordinates?.longitude ?? coordinates?.lng, -180, 180);

  if (cleanAddress) {
    let mParam = '';
    if (lat !== null && lng !== null) {
      mParam = `?m=${lng}%2C${lat}%2F16`;
    } else {
      const hasOtherRegion = OTHER_REGIONS_REGEX.test(cleanAddress);
      mParam = hasOtherRegion ? '' : `?m=${encodeURIComponent(DEFAULT_SAMARA_REGION_VIEWPORT)}`;
    }
    return `https://2gis.ru/search/${encodeURIComponent(cleanAddress)}${mParam}`;
  }

  if (lat !== null && lng !== null) {
    return `https://2gis.ru/geo/${lng},${lat}?m=${lng}%2C${lat}%2F16`;
  }

  return null;
};

/**
 * Ссылка на построение маршрута / открытие адреса в 2ГИС
 * Если передан адрес — ищет конкретное здание в 2ГИС с центрированием на координатах точки,
 * чтобы в 2ГИС отображалась точка на карте, название адреса в поле назначения, и карточка с кнопкой «Проехать».
 * Если текстового адреса нет — строит маршрут по координатам точки.
 */
export const getDgisRouteUrl = (pointOrAddress) => {
  if (!pointOrAddress) return null;

  let lat = null;
  let lng = null;
  let text = null;

  if (typeof pointOrAddress === 'object') {
    lat = normalizeDgisCoordinate(pointOrAddress.latitude ?? pointOrAddress.lat, -90, 90);
    lng = normalizeDgisCoordinate(pointOrAddress.longitude ?? pointOrAddress.lng, -180, 180);
    text = pointOrAddress.address || pointOrAddress.address_text || null;
  } else if (typeof pointOrAddress === 'string') {
    text = pointOrAddress;
  }

  const cleanText = text && String(text).trim();

  if (cleanText) {
    const coords = (lat !== null && lng !== null) ? { lat, lng } : null;
    return getDgisAddressUrl(cleanText, coords);
  }

  if (lat !== null && lng !== null) {
    return `https://2gis.ru/directions/points/%7C${lng}%2C${lat}?m=${lng}%2C${lat}%2F16`;
  }

  return null;
};

/**
 * Диплинк для открытия маршрута в мобильном приложении 2ГИС (iOS / Android)
 */
export const getDgisAppRouteUrl = (pointOrAddress) => {
  if (!pointOrAddress) return null;

  let lat = null;
  let lng = null;
  let text = null;

  if (typeof pointOrAddress === 'object') {
    lat = normalizeDgisCoordinate(pointOrAddress.latitude ?? pointOrAddress.lat, -90, 90);
    lng = normalizeDgisCoordinate(pointOrAddress.longitude ?? pointOrAddress.lng, -180, 180);
    text = pointOrAddress.address || pointOrAddress.address_text || null;
  } else if (typeof pointOrAddress === 'string') {
    text = pointOrAddress;
  }

  const cleanText = text && String(text).trim();
  if (cleanText) {
    return `dgis://2gis.ru/search/${encodeURIComponent(cleanText)}`;
  }

  if (lat !== null && lng !== null) {
    return `dgis://2gis.ru/routeSearch/rsType/car/to/${lng},${lat}`;
  }

  return null;
};
